/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  bible: {
    all: ["bible"] as const,
    /** Catálogo completo de versiones (settings + app-header COMPARTEN caché: misma query). */
    versions: (userId: string | undefined) => ["bible", "versions", userId] as const,
    /** Proyección ligera id/abbr/name para el lector. */
    versionsLite: () => ["bible", "versionsLite"] as const,
    /** Ficheros descargados de versiones (settings). */
    versionFiles: () => ["bible", "versionFiles"] as const,
    /** Highlights de un capítulo concreto en el lector. */
    chapterHighlights: (
      version: string | undefined,
      bookId: number | string | undefined,
      chapter: number | string | undefined,
      userId: string | undefined,
    ) => ["bible", "chapterHighlights", version, bookId, chapter, userId] as const,
    /** Conteo de highlights del usuario (profile stats). */
    highlightCount: (userId: string | undefined) => ["bible", "highlightCount", userId] as const,
    /** Conteo de notas del usuario (profile stats). */
    noteCount: (userId: string | undefined) => ["bible", "noteCount", userId] as const,
    /** Marcadores/favoritos de un capítulo concreto en el lector. */
    chapterBookmarks: (
      version: string | undefined,
      bookId: number | string | undefined,
      chapter: number | string | undefined,
      userId: string | undefined,
    ) => ["bible", "chapterBookmarks", version, bookId, chapter, userId] as const,
    /** Conteos por libro para la vista de analítica/progreso. */
    analyticsByBook: (kind: "bookmarks" | "highlights" | "notes", userId: string | undefined) =>
      ["bible", "analyticsByBook", kind, userId] as const,
  },
  bookmarks: {
    all: ["bookmarks"] as const,
    mine: (userId: string | undefined) => ["bookmarks", "mine", userId] as const,
    trashed: (userId: string | undefined) => ["bookmarks", "trashed", userId] as const,
  },
  notes: {
    all: ["notes"] as const,
    mine: (userId: string | undefined) => ["notes", "mine", userId] as const,
    tags: (userId: string | undefined) => ["notes", "tags", userId] as const,
    trashed: (userId: string | undefined) => ["notes", "trashed", userId] as const,
  },
  highlights: {
    all: ["highlights"] as const,
    mine: (bookFilter: string | number | undefined, userId: string | undefined) =>
      ["highlights", "mine", bookFilter, userId] as const,
    tags: (userId: string | undefined) => ["highlights", "tags", userId] as const,
    trashed: (userId: string | undefined) => ["highlights", "trashed", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (profile). */
    catalog: () => ["themes", "catalog"] as const,
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Tema activo del usuario (theme-provider). */
    userTheme: (userId: string | undefined) => ["themes", "userTheme", userId] as const,
    /** user_settings crudo (id de tema seleccionado). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  powersync: {
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Familia activa del usuario (selector de fuentes en profile). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Catálogos completos del selector de fuentes. */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Estado de juego (XP/nivel/racha de lectura/badges) derivado de las anotaciones. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
    /** Notas del usuario (señal de lectura). */
    notes: (userId: string | undefined) => ["gamification", "notes", userId] as const,
    /** Subrayados a nivel de versículo (señal de lectura). */
    verseHighlights: (userId: string | undefined) =>
      ["gamification", "verseHighlights", userId] as const,
    /** Subrayados de grupo (señal de lectura). */
    groupHighlights: (userId: string | undefined) =>
      ["gamification", "groupHighlights", userId] as const,
    /** Marcadores del usuario (señal de lectura). */
    bookmarks: (userId: string | undefined) => ["gamification", "bookmarks", userId] as const,
    /** Días de plan de lectura completados (señal de lectura). */
    planDays: (userId: string | undefined) => ["gamification", "planDays", userId] as const,
  },
  readingPlans: {
    all: ["readingPlans"] as const,
    /** Progreso (días completados) de TODOS los planes del usuario. */
    progress: (userId: string | undefined) => ["readingPlans", "progress", userId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
